import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import MarkedReact from "marked-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import {
  BoardAiApi,
  BoardAiEstimateResult,
  BoardAiTextResult,
  BoardAiTriageResult
} from "../../../services/board-ai-api";
import { BoardApi } from "../../../services/board-service-api";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import AiSparkleIcon from "./ai-sparkle-icon";
import { ArtifactOption, artifactKindHasPicker, loadArtifactOptions } from "./artifact-options";
import BoardAvatar from "./board-avatar";
import BoardDatePicker from "./board-date-picker";
import { boardKeys } from "./board-query-keys";
import { BoardService, MAX_ATTACHMENT_BYTES } from "./board-service";
import { detailShouldReload } from "./change-event-routing";
import { highlightMentions } from "./comment-composer";
import CommentEditor from "./comment-editor";
import { confirmDialog } from "./confirm-service";
import { currentUserId, currentUserName } from "./current-user";
import DebouncedRailInput from "./debounced-rail-input";
import { ActivitySkeleton, CommentsSkeleton, DescriptionSkeleton } from "./detail-skeleton";
import EmojiPicker from "./emoji-picker";
import LinkPreviewCard from "./link-preview-card";
import MarkdownField from "./markdown-field";
import { DirectoryMember, displayNameOf, getMembers, loadPeopleDirectory, subscribePeople } from "./people-directory";
import { createMarkdownRenderer, SAFE_MARKDOWN_RENDERER } from "./safe-markdown";
import StatusPicker, { StatusPickerOption } from "./status-picker";
import { availableTransitions } from "./transitions";
import {
  ARTIFACT_KIND_COLOR,
  ARTIFACT_KIND_LABELS,
  ArtifactKind,
  ArtifactLink,
  Attachment,
  BoardChangeEvent,
  BoardStatus,
  CommentReaction,
  CustomFieldDef,
  FieldChange,
  ISSUE_TYPE_LABELS,
  Issue,
  IssueActivity,
  IssueCard,
  IssueComment,
  IssueDetailBundle,
  IssueLink,
  IssueLinkType,
  IssuePriority,
  IssueType,
  LabelDef,
  LINK_INVERSE,
  LINK_TYPE_LABELS,
  PRIORITY_COLOR,
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  Project,
  UpdateIssueInput,
  Version
} from "./types";
import { useBoardConfig } from "./use-board";
import { useBoardAiEnabled } from "./use-board-ai";

interface AttachmentRowProps {
  attachment: Attachment;
  onRemove: () => void;
  onPreview: (attachment: Attachment) => void;
}

interface PendingUpload {
  id: number;
  name: string;
  size: number;
}

type PreviewKind = "image" | "video" | "audio" | "pdf";

// What an attachment can be previewed AS in the browser, or null if it must be downloaded.
function previewKind(mimeType: string, ext: string): PreviewKind | null {
  const mime: string = (mimeType || "").toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf" || ext === "PDF") return "pdf";
  return null;
}

// The extension shown in the type chip (uppercased, ≤4 chars). "FILE" when there's none.
function fileExtension(fileName: string): string {
  const dot: number = fileName.lastIndexOf(".");
  if (dot < 0 || dot === fileName.length - 1) return "FILE";
  return fileName
    .slice(dot + 1)
    .toUpperCase()
    .slice(0, 4);
}

// Coarse type bucket that drives the tile's accent colour (see [data-kind] in board.scss).
function attachmentKind(mimeType: string, ext: string): string {
  const mime: string = (mimeType || "").toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/") || mime.startsWith("audio/")) return "media";
  if (mime === "application/pdf" || ext === "PDF") return "pdf";
  if (["ZIP", "RAR", "7Z", "TAR", "GZ"].includes(ext)) return "archive";
  if (["XLS", "XLSX", "CSV"].includes(ext)) return "sheet";
  if (["JS", "TS", "TSX", "JSX", "JSON", "CS", "HTML", "CSS", "PY", "SQL"].includes(ext)) return "code";
  if (mime.startsWith("text/") || ["DOC", "DOCX", "TXT", "MD", "RTF"].includes(ext)) return "doc";
  return "file";
}

// A download glyph (arrow into a tray) — no matching name in the icon set, so inline.
const DownloadGlyph: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v11" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 20h14" />
  </svg>
);

// Triggers a browser download of an attachment under its original filename (the presigned
// URL carries Content-Disposition: attachment).
async function downloadAttachment(attachmentId: string): Promise<void> {
  const href: string | null = await BoardApi.getAttachmentContent(attachmentId, false);
  if (!href) return;
  const anchor: HTMLAnchorElement = document.createElement("a");
  anchor.href = href;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

// One attachment: a type-coloured tile (image thumbnail or extension chip), name + size, and
// hover-revealed download / remove. Previewable types (image/gif/video/audio/pdf) open in the
// in-app lightbox on click; everything else downloads.
const AttachmentRow: React.FC<AttachmentRowProps> = ({ attachment, onRemove, onPreview }) => {
  const isImage: boolean = (attachment.mimeType ?? "").startsWith("image/");
  const ext: string = fileExtension(attachment.fileName);
  const kind: string = attachmentKind(attachment.mimeType ?? "", ext);
  const canPreview: boolean = previewKind(attachment.mimeType ?? "", ext) !== null;
  const [thumb, setThumb] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  useEffect(() => {
    if (!isImage) return undefined;
    let cancelled: boolean = false;
    void BoardApi.getAttachmentContent(attachment.id, true).then((next: string | null) => {
      if (!cancelled) setThumb(next);
    });
    return () => {
      cancelled = true;
    };
  }, [attachment.id, isImage]);

  const open = (): void => {
    if (canPreview) {
      onPreview(attachment);
      return;
    }
    setBusy(true);
    void downloadAttachment(attachment.id).finally(() => setBusy(false));
  };

  const download = (): void => {
    setBusy(true);
    void downloadAttachment(attachment.id).finally(() => setBusy(false));
  };

  const openTitle: string = canPreview ? `Preview ${attachment.fileName}` : `Download ${attachment.fileName}`;

  return (
    <div className="kuika-board__attachment">
      <button
        type="button"
        className="kuika-board__attachment-tile"
        data-kind={kind}
        onClick={open}
        disabled={busy}
        title={openTitle}
      >
        {isImage && thumb ? (
          <img src={thumb} alt={attachment.fileName} />
        ) : (
          <span className="kuika-board__attachment-chip">{ext}</span>
        )}
      </button>
      <button type="button" className="kuika-board__attachment-meta" onClick={open} disabled={busy} title={openTitle}>
        <span className="kuika-board__attachment-name">{attachment.fileName}</span>
        <span className="kuika-board__attachment-size">
          {ext} · {formatBytes(attachment.sizeBytes)}
          {canPreview ? " · preview" : ""}
        </span>
      </button>
      <div className="kuika-board__attachment-actions">
        <button
          type="button"
          className="kuika-board__attachment-action"
          onClick={download}
          disabled={busy}
          aria-label="Download attachment"
          title="Download"
        >
          <DownloadGlyph />
        </button>
        <button
          type="button"
          className="kuika-board__attachment-action kuika-board__attachment-action--danger"
          onClick={onRemove}
          aria-label="Remove attachment"
          title="Remove"
        >
          <KuikaSvg name="trash" width={14} height={14} />
        </button>
      </div>
    </div>
  );
};

interface AttachmentLightboxProps {
  attachment: Attachment;
  onClose: () => void;
}

// Full-viewport media preview, portaled to <body> so the issue drawer's overflow/transform
// doesn't clip it. Fetches an inline presigned URL (so PDFs/video render rather than download)
// and shows the right element per type. Closes on backdrop click or Escape.
const AttachmentLightbox: React.FC<AttachmentLightboxProps> = ({ attachment, onClose }) => {
  const ext: string = fileExtension(attachment.fileName);
  const kind: PreviewKind | null = previewKind(attachment.mimeType ?? "", ext);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled: boolean = false;
    void BoardApi.getAttachmentContent(attachment.id, true).then((next: string | null) => {
      if (!cancelled) setUrl(next);
    });
    return () => {
      cancelled = true;
    };
  }, [attachment.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const stop = (event: React.MouseEvent): void => event.stopPropagation();

  return createPortal(
    <div className="kuika-board__lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <div className="kuika-board__lightbox-bar" onClick={stop}>
        <span className="kuika-board__lightbox-name" title={attachment.fileName}>
          {attachment.fileName}
        </span>
        <div className="kuika-board__lightbox-actions">
          <button
            type="button"
            className="kuika-board__lightbox-button"
            onClick={() => void downloadAttachment(attachment.id)}
            aria-label="Download"
            title="Download"
          >
            <DownloadGlyph />
          </button>
          <button
            type="button"
            className="kuika-board__lightbox-button"
            onClick={onClose}
            aria-label="Close preview"
            title="Close"
          >
            <KuikaSvg name="x" width={16} height={16} />
          </button>
        </div>
      </div>
      <div className="kuika-board__lightbox-stage" onClick={stop}>
        {!url ? (
          <span className="kuika-board__attachment-spinner kuika-board__lightbox-spinner" />
        ) : kind === "image" ? (
          <img className="kuika-board__lightbox-media" src={url} alt={attachment.fileName} />
        ) : kind === "video" ? (
          <video className="kuika-board__lightbox-media" src={url} controls autoPlay />
        ) : kind === "audio" ? (
          <audio src={url} controls />
        ) : kind === "pdf" ? (
          <iframe className="kuika-board__lightbox-frame" src={url} title={attachment.fileName} />
        ) : null}
      </div>
    </div>,
    document.body
  );
};

// Child issues queued on a NEW epic draft (before it exists). board-page owns the state and commits
// them once the epic is created. `existing` are issues to link; `toCreate` are summaries to create.
export interface NewEpicChildren {
  existing: IssueCard[];
  toCreate: string[];
  addExisting: (card: IssueCard) => void;
  removeExisting: (id: string) => void;
  queueCreate: (summary: string) => void;
  removeCreate: (index: number) => void;
}

export interface IssueDetailProps {
  issue: Issue;
  statuses: BoardStatus[];
  project: Project | undefined;
  layout: "drawer" | "page";
  // Optimistically patch the issue the parent holds (board-page's openIssue / full-page's issue),
  // so rail edits (labels, dates, selects, …) reflect immediately instead of waiting for the server
  // round-trip + reload. The authoritative reload later overwrites with the confirmed value.
  onPatchIssue?: (patch: UpdateIssueInput) => void;
  // Draft/create mode: `issue` is an unsaved draft (no id). Field edits update the draft via
  // onPatchIssue instead of persisting, the detail bundle (comments/activity/links/…) isn't
  // fetched, and id-dependent sections are hidden. The parent's Create action persists it.
  isNewIssue?: boolean;
  // Whether `issue` carries the full record (incl. descriptionMarkdown) yet. The drawer seeds
  // `issue` from a light card (no description) for instant appearance, then hydrates it via a
  // separate by-key fetch; until that lands, the description editor shows a skeleton rather than
  // flashing its empty-state placeholder. Defaults to true (callers that already hold a fully
  // resolved issue — e.g. the full page — need no gate).
  descriptionReady?: boolean;
  // Present only in new-issue + epic mode: lets the draft queue child issues before the epic exists.
  newEpicChildren?: NewEpicChildren;
}

const LINK_TYPES: IssueLinkType[] = ["blocks", "blocked_by", "relates_to", "duplicates", "duplicated_by"];

function parseMinutes(input: string): number | null {
  const trimmed: string = input.trim();
  if (!trimmed) return null;
  let total: number = 0;
  const regex: RegExp = /(\d+)\s*([hmd])?/gi;
  let match: RegExpExecArray | null;
  let matched: boolean = false;
  while ((match = regex.exec(trimmed)) !== null) {
    matched = true;
    const value: number = Number(match[1]);
    const unit: string = (match[2] ?? "m").toLowerCase();
    if (unit === "h") total += value * 60;
    else if (unit === "d") total += value * 60 * 8;
    else total += value;
  }
  if (!matched) {
    const fallback: number = Number(trimmed);
    if (Number.isNaN(fallback)) return null;
    return fallback;
  }
  return total;
}

function formatMinutes(value: number): string {
  if (value <= 0) return "0m";
  const hours: number = Math.floor(value / 60);
  const minutes: number = value % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

// The http(s) URLs in a comment, deduped and capped, for link-preview cards. Trailing sentence
// punctuation and any markdown closing chars are trimmed off the match.
function extractCommentUrls(markdown: string): string[] {
  const matches: RegExpMatchArray | null = markdown.match(/\bhttps?:\/\/[^\s<>()[\]"']+/gi);
  if (!matches) return [];
  const seen: Set<string> = new Set<string>();
  const urls: string[] = [];
  for (const raw of matches) {
    const url: string = raw.replace(/[.,;:!?]+$/, "");
    if (seen.has(url)) continue;
    seen.add(url);
    urls.push(url);
    if (urls.length >= 3) break;
  }
  return urls;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb: number = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// Compact relative timestamp for the history timeline (absolute date past a week). No dayjs
// relativeTime-plugin dependency, so it works regardless of whether that plugin is extended.
function relativeTime(iso: string): string {
  const then: number = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds: number = Math.round((Date.now() - then) / 1000);
  if (seconds < 45) return "just now";
  const minutes: number = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours: number = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days: number = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return dayjs(iso).format("MMM D, YYYY");
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader: FileReader = new FileReader();
    reader.onload = (): void => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = (): void => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const TYPES: IssueType[] = ["task", "feature", "bug", "chore", "epic"];
// Epic swatch palette (JIRA-style). The chosen colour is shown wherever the epic appears.
const EPIC_COLORS: string[] = [
  "var(--purple-5)",
  "var(--blue-5)",
  "var(--green-6)",
  "var(--orange-5)",
  "var(--pink-5)",
  "var(--red-5)",
  "var(--yellow-6)",
  "var(--dark-7)"
];

// Resizable detail rail (JIRA-style), width persisted as a user preference.
const RAIL_WIDTH_KEY: string = "kuika:board:rail-width";
const RAIL_MIN_WIDTH: number = 240;
const RAIL_MAX_WIDTH: number = 520;
const RAIL_DEFAULT_WIDTH: number = 320;

function clampRailWidth(value: number): number {
  return Math.min(RAIL_MAX_WIDTH, Math.max(RAIL_MIN_WIDTH, value));
}

function getStoredRailWidth(): number {
  try {
    const raw: string | null = window.localStorage.getItem(RAIL_WIDTH_KEY);
    const parsed: number = raw ? Number(raw) : NaN;
    return Number.isNaN(parsed) ? RAIL_DEFAULT_WIDTH : clampRailWidth(parsed);
  } catch {
    return RAIL_DEFAULT_WIDTH;
  }
}

function setStoredRailWidth(value: number): void {
  try {
    window.localStorage.setItem(RAIL_WIDTH_KEY, String(value));
  } catch {
    // ignore quota / privacy errors
  }
}

const IssueDetail: React.FC<IssueDetailProps> = ({
  issue,
  statuses,
  project,
  layout,
  onPatchIssue,
  isNewIssue = false,
  descriptionReady = true,
  newEpicChildren
}) => {
  // All field edits go through this: optimistically patch the issue the parent holds (so the change
  // shows instantly) and persist via the optimistic updateIssue. In new-issue mode there's no row
  // to persist to yet — edits only update the parent's draft; the Create action persists it.
  const patchIssue = (patch: UpdateIssueInput): void => {
    onPatchIssue?.(patch);
    if (!isNewIssue) {
      void BoardService.updateIssue(issue.id, patch);
    }
  };
  // A draft has no id yet, so anything id-scoped is unavailable until it's created. Named
  // capabilities (rather than scattered isNewIssue checks) decide what the body renders.
  const isSaved: boolean = !isNewIssue;
  const canDiscuss: boolean = isSaved; // comments / activity / attachments / links / subtasks
  const canUseIssueAi: boolean = isSaved; // AI features keyed by the issue id (draft/summarize/estimate/suggest)
  const { config } = useBoardConfig();
  const queryClient: QueryClient = useQueryClient();
  // Per-issue data (comments/activity/links/subtasks/attachments) is cached by TanStack Query,
  // keyed by issue id. It refetches only on mutations that touch THIS issue (see
  // detailShouldReload) — a change to another card, or a config edit, leaves the cache intact.
  // The same predicate handles both local edits and teammates' edits arriving over SSE, since
  // both route through BoardService.
  const detailQuery = useQuery({
    queryKey: boardKeys.detail(issue.id),
    queryFn: (): Promise<IssueDetailBundle> => BoardApi.getIssueDetail(issue.id),
    // A draft has no id and no server-side detail to fetch.
    enabled: !isNewIssue
  });
  // On error BoardApi already toasted; TanStack keeps the last successful bundle cached.
  const bundle: IssueDetailBundle | null = detailQuery.data ?? null;
  useEffect(() => {
    const unsubscribe: () => void = BoardService.subscribe((event: BoardChangeEvent) => {
      if (detailShouldReload(event, issue.id)) {
        void queryClient.invalidateQueries({ queryKey: boardKeys.detail(issue.id) });
      }
    });
    return unsubscribe;
  }, [queryClient, issue.id]);
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();
  const [summary, setSummary] = useState<string>(issue.summary);
  const [subtaskDraft, setSubtaskDraft] = useState<string>("");
  const [linkType, setLinkType] = useState<IssueLinkType>("blocks");
  const [linkTargetKey, setLinkTargetKey] = useState<string>("");
  const [linkError, setLinkError] = useState<string>("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  // Which comment's emoji-reaction picker is open (commentId), or null.
  const [reactionPickerFor, setReactionPickerFor] = useState<string | null>(null);
  // `${commentId}|${url}` keys for link previews the viewer dismissed (ephemeral, per session).
  const [dismissedPreviews, setDismissedPreviews] = useState<Set<string>>(() => new Set<string>());
  const [activityTab, setActivityTab] = useState<"comments" | "history">("comments");
  const aiEnabled: boolean = useBoardAiEnabled();
  // "Catch me up" summary of the issue + its discussion (null until requested).
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiSummaryBusy, setAiSummaryBusy] = useState<boolean>(false);
  // AI assist for the rail/body: drafted description, suggested subtasks (pending acceptance),
  // and a proposed story-point estimate (pending apply).
  const [aiDescribeBusy, setAiDescribeBusy] = useState<boolean>(false);
  const [aiSubtasks, setAiSubtasks] = useState<string[]>([]);
  const [aiSubtasksBusy, setAiSubtasksBusy] = useState<boolean>(false);
  const [aiEstimate, setAiEstimate] = useState<BoardAiEstimateResult | null>(null);
  const [aiEstimateBusy, setAiEstimateBusy] = useState<boolean>(false);
  // Create-mode AI: triage fills type/priority/labels/description from the summary (on demand).
  const [triageBusy, setTriageBusy] = useState<boolean>(false);
  const [artifactKind, setArtifactKind] = useState<ArtifactKind>("screen");
  const [artifactName, setArtifactName] = useState<string>("");
  // Options for the picker of the kind currently being ADDED.
  const [addOptions, setAddOptions] = useState<ArtifactOption[]>([]);
  // Current names of already-linked artifacts, keyed `${kind}:${refId}`, so links survive renames
  // (the stored displayName is only a fallback). Resolved by loading each linked kind's list.
  const [artifactNames, setArtifactNames] = useState<Record<string, string>>({});
  // Bumped after each add so the transient link/artifact pickers remount and clear their
  // selection + search text (KuikaSelectbox keeps internal state and won't reset from value=undefined).
  const [linkPickerNonce, setLinkPickerNonce] = useState<number>(0);
  const [artifactPickerNonce, setArtifactPickerNonce] = useState<number>(0);
  const [labelDraft, setLabelDraft] = useState<string>("");
  const [isDraggingFile, setIsDraggingFile] = useState<boolean>(false);
  const [attachmentError, setAttachmentError] = useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  // In-flight uploads, shown as placeholder cards until the bundle refetch brings the real
  // attachment. Keyed by a monotonic local id (the file has no server id yet).
  const [uploads, setUploads] = useState<PendingUpload[]>([]);
  const uploadSeq = React.useRef<number>(0);
  // The attachment currently open in the preview lightbox (null = closed).
  const [previewing, setPreviewing] = useState<Attachment | null>(null);
  const [railWidth, setRailWidth] = useState<number>(() => getStoredRailWidth());

  // Workspace members for the assignee picker (loaded once; re-renders when ready).
  const [members, setMembers] = useState<DirectoryMember[]>(() => getMembers());
  useEffect(() => {
    const unsubscribe: () => void = subscribePeople(() => setMembers(getMembers()));
    void loadPeopleDirectory();
    return unsubscribe;
  }, []);
  const assigneeOptions = useMemo(() => {
    const byId: Map<string, string> = new Map<string, string>();
    for (const member of members) byId.set(member.id, member.name);
    // Keep the current assignee selectable even if they're not a current member.
    if (issue.assigneeUserId && !byId.has(issue.assigneeUserId)) {
      byId.set(issue.assigneeUserId, displayNameOf(issue.assigneeUserId));
    }
    const unassigned = {
      value: "",
      searchLabel: "Unassigned",
      label: (
        <span className="kuika-board__assignee-option kuika-board__assignee-option--muted">
          <span className="kuika-board__rail-avatar kuika-board__rail-avatar--empty" aria-hidden="true" />
          Unassigned
        </span>
      )
    };
    const memberOptions = Array.from(byId.entries())
      .sort((a: [string, string], b: [string, string]) => a[1].localeCompare(b[1]))
      .map(([userId, name]: [string, string]) => ({
        value: userId,
        searchLabel: name,
        label: (
          <span className="kuika-board__assignee-option">
            <BoardAvatar userId={userId} className="kuika-board__rail-avatar" ariaHidden />
            {name}
          </span>
        )
      }));
    return [unassigned, ...memberOptions];
  }, [members, issue.assigneeUserId]);

  // Member options for "user"-typed custom fields (no issue-assignee special-casing).
  const userFieldOptions = useMemo(() => {
    const empty = {
      value: "",
      searchLabel: "—",
      label: (
        <span className="kuika-board__assignee-option kuika-board__assignee-option--muted">
          <span className="kuika-board__rail-avatar kuika-board__rail-avatar--empty" aria-hidden="true" />—
        </span>
      )
    };
    const memberOptions = members
      .slice()
      .sort((a: DirectoryMember, b: DirectoryMember) => a.name.localeCompare(b.name))
      .map((member: DirectoryMember) => ({
        value: member.id,
        searchLabel: member.name,
        label: (
          <span className="kuika-board__assignee-option">
            <BoardAvatar userId={member.id} className="kuika-board__rail-avatar" ariaHidden />
            {member.name}
          </span>
        )
      }));
    return [empty, ...memberOptions];
  }, [members]);

  // Link-target and Epic pickers search the server on demand instead of scanning every issue.
  const [linkQuery, setLinkQuery] = useState<string>("");
  const [linkResults, setLinkResults] = useState<IssueCard[]>([]);
  useEffect(() => {
    const q: string = linkQuery.trim();
    if (!q) {
      setLinkResults([]);
      return;
    }
    let cancelled: boolean = false;
    const timer: number = window.setTimeout(async () => {
      const results: IssueCard[] = await BoardApi.searchIssues(q, 10);
      if (!cancelled) setLinkResults(results.filter((card: IssueCard) => card.id !== issue.id));
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [linkQuery, issue.id]);

  const [epicQuery, setEpicQuery] = useState<string>("");
  const [epicResults, setEpicResults] = useState<IssueCard[]>([]);
  useEffect(() => {
    const q: string = epicQuery.trim();
    if (!q) {
      setEpicResults([]);
      return;
    }
    let cancelled: boolean = false;
    const timer: number = window.setTimeout(async () => {
      const results: IssueCard[] = await BoardApi.searchIssues(q, 10);
      if (!cancelled) {
        setEpicResults(
          results.filter(
            (card: IssueCard) => card.type === "epic" && card.projectKey === issue.projectKey && card.id !== issue.id
          )
        );
      }
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [epicQuery, issue.projectKey, issue.id]);

  // "Add existing issue to epic" search (shown when this issue is an epic): non-epic issues in the
  // same project, not already in this epic. Summary OR key match, reusing the server search.
  const [childQuery, setChildQuery] = useState<string>("");
  const [childResults, setChildResults] = useState<IssueCard[]>([]);
  const [childSearching, setChildSearching] = useState<boolean>(false);
  const [childDraft, setChildDraft] = useState<string>("");
  // Bumped after each "add existing" pick to remount the picker — the selectbox holds its own
  // selection internally and our constant value="" can't reset it, so it would otherwise show the
  // selected (now-excluded) issue's raw id.
  const [childAddNonce, setChildAddNonce] = useState<number>(0);
  useEffect(() => {
    const q: string = childQuery.trim();
    if (!q || issue.type !== "epic") {
      setChildResults([]);
      setChildSearching(false);
      return;
    }
    setChildSearching(true);
    let cancelled: boolean = false;
    const timer: number = window.setTimeout(async () => {
      const results: IssueCard[] = await BoardApi.searchIssues(q, 10);
      if (!cancelled) {
        setChildResults(
          results.filter(
            (card: IssueCard) =>
              card.projectKey === issue.projectKey &&
              card.type !== "epic" &&
              card.id !== issue.id &&
              card.epicId !== issue.id
          )
        );
        setChildSearching(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [childQuery, issue.type, issue.projectKey, issue.id]);

  // Options for the "add existing issue" picker: a disabled loading/empty sentinel while searching or
  // when nothing matches, else the matching issues each tagged with their type icon. `excludeIds`
  // drops issues already queued/linked.
  const childSearchOptions = (
    excludeIds: Set<string>
  ): { value: string; label: React.ReactNode; disabled?: boolean }[] => {
    if (!childQuery.trim()) return [{ value: "__hint__", label: "Type to search issues…", disabled: true }];
    if (childSearching) return [{ value: "__loading__", label: "Searching…", disabled: true }];
    const matches: IssueCard[] = childResults.filter((c: IssueCard) => !excludeIds.has(c.id));
    if (matches.length === 0) return [{ value: "__none__", label: "No matching issues", disabled: true }];
    return matches.map((entry: IssueCard) => ({
      value: entry.id,
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span className={`kuika-board__type-icon kuika-board__type-icon--${entry.type}`} aria-hidden="true" />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {entry.key} · {entry.summary}
          </span>
        </span>
      )
    }));
  };

  const onRailResizeDown = (event: React.PointerEvent): void => {
    event.preventDefault();
    const startX: number = event.clientX;
    const startWidth: number = railWidth;
    let latest: number = startWidth;
    const onMove = (moveEvent: PointerEvent): void => {
      // Rail is on the right, so dragging the handle left widens it.
      latest = clampRailWidth(startWidth + (startX - moveEvent.clientX));
      setRailWidth(latest);
    };
    const onUp = (): void => {
      setStoredRailWidth(latest);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const subtasks: Issue[] = bundle?.subtasks ?? [];

  // Linked issues come from the detail bundle (links + light cards for the "other" issue),
  // so we never need every issue in memory.
  const links: Array<{ link: IssueLink; other: IssueCard; relation: IssueLinkType }> = useMemo(() => {
    if (!bundle) return [];
    const cardById: Map<string, IssueCard> = new Map(bundle.linkedIssues.map((card: IssueCard) => [card.id, card]));
    const result: Array<{ link: IssueLink; other: IssueCard; relation: IssueLinkType }> = [];
    for (const link of bundle.links) {
      if (link.fromIssueId === issue.id) {
        const other: IssueCard | undefined = cardById.get(link.toIssueId);
        if (other) result.push({ link, other, relation: link.type });
      } else if (link.toIssueId === issue.id) {
        const other: IssueCard | undefined = cardById.get(link.fromIssueId);
        // Render the inverse relation from this issue's perspective.
        if (other) result.push({ link, other, relation: LINK_INVERSE[link.type] });
      }
    }
    return result;
  }, [bundle, issue.id]);

  const attachments: Attachment[] = bundle?.attachments ?? [];
  // Server returns artifact links already scoped to this issue.
  const artifactLinks: ArtifactLink[] = bundle?.artifactLinks ?? [];
  // The current epic (labelled card) so the Epic picker shows the selection even before searching.
  const epicCard: IssueCard | null = bundle?.epic ?? null;
  // When this issue IS an epic, its child issues (the "Child issues" section).
  const epicChildren: IssueCard[] = bundle?.epicChildren ?? [];

  // The platform (designer) project this issue's board project is tied to. Drives the
  // linked-artifact autocomplete (screens) + correct screen-id refs. Undefined until the
  // board project has been linked (onboarding).
  const platformProjectId: string | undefined = useMemo(
    () => config.projects.find((entry: Project) => entry.key === issue.projectKey)?.platformProjectId,
    [config.projects, issue.projectKey]
  );

  // Custom fields are project-scoped — show only the issue's own project's fields.
  // This project's fields, scoped to the issue's type: a field with no issueTypes applies to every
  // type, otherwise only to the types it lists (JIRA-style per-type field configuration).
  const projectCustomFields: CustomFieldDef[] = useMemo(
    () =>
      config.customFieldDefs.filter(
        (def: CustomFieldDef) =>
          def.projectKey === issue.projectKey && (def.issueTypes.length === 0 || def.issueTypes.includes(issue.type))
      ),
    [config.customFieldDefs, issue.projectKey, issue.type]
  );

  // Load the picker options for the kind being ADDED (the listable kinds: screen/datasource/api/
  // workflow). The picker stores the artifact's stable id as the ref. Free-text kinds clear it.
  useEffect(() => {
    if (!artifactKindHasPicker(artifactKind) || !platformProjectId) {
      setAddOptions([]);
      return;
    }
    let cancelled: boolean = false;
    void loadArtifactOptions(artifactKind, platformProjectId).then((options: ArtifactOption[]) => {
      if (!cancelled) setAddOptions(options);
    });
    return () => {
      cancelled = true;
    };
  }, [artifactKind, platformProjectId]);

  // Resolve the CURRENT name of each linked artifact so links survive renames (the stored
  // displayName is only a fallback). Load each listable kind present in the links once and key the
  // names by `${kind}:${refId}`.
  useEffect(() => {
    if (!platformProjectId) return;
    const kinds: ArtifactKind[] = Array.from(
      new Set(artifactLinks.map((link: ArtifactLink) => link.kind).filter(artifactKindHasPicker))
    );
    if (kinds.length === 0) return;
    let cancelled: boolean = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const kind of kinds) {
        const options: ArtifactOption[] = await loadArtifactOptions(kind, platformProjectId);
        for (const option of options) next[`${kind}:${option.id}`] = option.name;
      }
      if (!cancelled) setArtifactNames(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [artifactLinks, platformProjectId]);

  const ingestFiles = async (files: FileList | File[]): Promise<void> => {
    setAttachmentError("");
    const list: File[] = Array.from(files);
    for (const file of list) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setAttachmentError(
          `"${file.name}" is ${formatBytes(file.size)} — over the ${formatBytes(MAX_ATTACHMENT_BYTES)} limit.`
        );
        continue;
      }
      const dataUrl: string = await readFileAsDataUrl(file);
      if (!dataUrl) continue;
      // Show a placeholder card for the duration of the upload; the attachments.changed refetch
      // replaces it with the real attachment once the bytes land in storage.
      const id: number = (uploadSeq.current += 1);
      setUploads((prev: PendingUpload[]) => [...prev, { id, name: file.name, size: file.size }]);
      try {
        const created: Attachment | null = await BoardService.addAttachment(
          issue.id,
          file.name,
          file.type || "application/octet-stream",
          dataUrl,
          file.size
        );
        if (!created) setAttachmentError(`Couldn't upload "${file.name}". Please try again.`);
      } finally {
        setUploads((prev: PendingUpload[]) => prev.filter((upload: PendingUpload) => upload.id !== id));
      }
    }
  };

  const doneStatusId: string = statuses.find((status: BoardStatus) => status.category === "done")?.id ?? "";

  // The issue's current status (the picker trigger).
  const currentStatus: BoardStatus | undefined = useMemo(
    () => statuses.find((status: BoardStatus) => status.id === issue.statusId),
    [statuses, issue.statusId]
  );
  // The picker's options. A project with a workflow shows its named transitions (JIRA-style); a
  // new-issue draft or an unrestricted project shows the statuses themselves.
  const statusOptions: StatusPickerOption[] = useMemo(() => {
    const hasWorkflow: boolean = (project?.transitions?.length ?? 0) > 0;
    if (isNewIssue || !hasWorkflow) {
      return statuses.map((status: BoardStatus) => ({ id: status.id, label: status.name, category: status.category }));
    }
    const options: StatusPickerOption[] = [];
    for (const transition of availableTransitions(project, issue.statusId)) {
      const target: BoardStatus | undefined = statuses.find((s: BoardStatus) => s.id === transition.toStatusId);
      if (!target) continue;
      const name: string = (transition.name ?? "").trim();
      options.push({
        id: target.id,
        label: name || target.name,
        category: target.category,
        sublabel: name ? target.name : undefined
      });
    }
    return options;
  }, [isNewIssue, project, statuses, issue.statusId]);
  // Change status from the rail. A draft updates locally. A saved issue moves optimistically — patch
  // the held issue for an instant chip update (display-only, NOT updateIssue, which would bypass the
  // workflow gating), persist via moveIssue, and revert the optimistic patch if the server rejects.
  const pickStatus = (statusId: string): void => {
    if (statusId === issue.statusId) return;
    if (isNewIssue) {
      patchIssue({ statusId });
      return;
    }
    const previous: string = issue.statusId;
    onPatchIssue?.({ statusId });
    void BoardService.moveIssue({
      issueId: issue.id,
      destinationStatusId: statusId,
      destinationIndex: 0,
      sourceStatusId: previous
    }).then((result: Issue | null) => {
      if (!result) onPatchIssue?.({ statusId: previous });
    });
  };

  // Mention candidates come from the workspace member directory plus this issue's own
  // reporter/assignee — no longer derived from scanning every issue.
  // Real project keys in the workspace — used to promote bare issue keys (KUI-12) in comment and
  // description text to clickable chips (so prose like "ISO-9001" stays plain).
  const issueKeySet: Set<string> = useMemo(
    () => new Set<string>(config.projects.map((p: Project) => p.key)),
    [config.projects]
  );
  const openIssueByKey = useCallback((key: string) => navigate(`/${domainName}/issue/${key}`), [navigate, domainName]);
  const commentRenderer: object = useMemo(
    () => createMarkdownRenderer({ issueKeys: issueKeySet, onIssueKey: openIssueByKey }),
    [issueKeySet, openIssueByKey]
  );

  const mentionCandidates: string[] = useMemo(() => {
    const names: Set<string> = new Set<string>([
      currentUserName(),
      ...members.map((member: DirectoryMember) => member.name)
    ]);
    if (issue.assigneeUserId) names.add(displayNameOf(issue.assigneeUserId));
    if (issue.reporterUserId) names.add(displayNameOf(issue.reporterUserId));
    names.delete("");
    return Array.from(names).sort();
  }, [members, issue.assigneeUserId, issue.reporterUserId]);

  useEffect(() => {
    setSummary(issue.summary);
  }, [issue.id]);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent): void => {
      const files: FileList | undefined = event.clipboardData?.files;
      if (files && files.length > 0) {
        event.preventDefault();
        void ingestFiles(files);
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [issue.id]);

  // Comments come issue-scoped + chronological from the bundle; activity is reversed to
  // newest-first for the timeline.
  const issueComments: IssueComment[] = bundle?.comments ?? [];
  const issueActivity: IssueActivity[] = useMemo(() => (bundle?.activity ?? []).slice().reverse(), [bundle]);
  // The per-issue bundle (comments/activity/links/…) is still loading until it first resolves.
  // No bundle is fetched for a draft, so it's never "loading".
  const detailLoading: boolean = !isNewIssue && bundle === null;
  // The description rides a different fetch than the bundle (the parent's by-key hydration, not
  // getIssueDetail), so it has its own gate: a draft is always ready, otherwise wait until the
  // parent reports the full issue is in. Gating on the bundle here would race — the bundle can
  // resolve first and unmask the editor's placeholder before the description text has arrived.
  const descriptionLoading: boolean = !isNewIssue && !descriptionReady;

  const commitSummary = (): void => {
    const trimmed: string = summary.trim() || "Untitled";
    if (trimmed !== issue.summary) {
      patchIssue({ summary: trimmed });
    }
  };

  const commitStoryPoints = (raw: string): void => {
    const parsed: number = Number(raw);
    const next: number | null = raw === "" || Number.isNaN(parsed) ? null : parsed;
    if (next !== (issue.storyPoints ?? null)) {
      patchIssue({ storyPoints: next });
    }
  };

  // ── AI assist (rail/body) ────────────────────────────────────────────────
  const handleAiDescribe = (): void => {
    if (aiDescribeBusy) return;
    setAiDescribeBusy(true);
    // A draft has no id yet, so describe from the summary/type; a saved issue describes by id.
    const request = isNewIssue ? { summary, type: issue.type } : { issueId: issue.id };
    void BoardAiApi.describe(request)
      .then((result): void => {
        patchIssue({ descriptionMarkdown: result.descriptionMarkdown });
      })
      .catch((): void => undefined)
      .finally((): void => setAiDescribeBusy(false));
  };

  const handleAiSubtasks = (): void => {
    if (aiSubtasksBusy) return;
    setAiSubtasksBusy(true);
    void BoardAiApi.suggestSubtasks(issue.id)
      .then((result): void => setAiSubtasks(result.subtasks.map((entry): string => entry.summary)))
      .catch((): void => undefined)
      .finally((): void => setAiSubtasksBusy(false));
  };

  const acceptAiSubtask = (summary: string): void => {
    void BoardService.createSubtask(issue.id, summary);
    setAiSubtasks((prev: string[]): string[] => prev.filter((entry: string): boolean => entry !== summary));
  };

  const handleAiEstimate = (): void => {
    if (aiEstimateBusy) return;
    setAiEstimateBusy(true);
    void BoardAiApi.estimate({ issueId: issue.id })
      .then((result: BoardAiEstimateResult): void => setAiEstimate(result))
      .catch((): void => undefined)
      .finally((): void => setAiEstimateBusy(false));
  };

  const applyAiEstimate = (): void => {
    if (aiEstimate?.storyPoints != null) {
      patchIssue({ storyPoints: aiEstimate.storyPoints });
    }
    setAiEstimate(null);
  };

  // ── Create-mode AI (draft only): triage fills fields from the summary, on demand ──
  const handleTriage = (): void => {
    const trimmed: string = summary.trim();
    if (!trimmed || triageBusy) return;
    setTriageBusy(true);
    void BoardAiApi.triage(trimmed, issue.descriptionMarkdown)
      .then((result: BoardAiTriageResult): void => {
        if (result.summary) setSummary(result.summary);
        patchIssue({
          summary: result.summary || trimmed,
          type: result.type,
          priority: result.priority,
          labels: result.labels,
          descriptionMarkdown: result.descriptionMarkdown || issue.descriptionMarkdown
        });
      })
      .catch((): void => undefined)
      .finally((): void => setTriageBusy(false));
  };

  // Patch the open issue's cached detail bundle in place so a detail edit shows instantly; the
  // mutation's unconditional refresh refetches a moment later — reconciling to server truth on
  // success and rolling the patch back on failure.
  const patchBundle = (mutate: (prev: IssueDetailBundle) => IssueDetailBundle): void => {
    queryClient.setQueryData<IssueDetailBundle>(boardKeys.detail(issue.id), (prev: IssueDetailBundle | undefined) =>
      prev ? mutate(prev) : prev
    );
  };

  // ── Epic child-issue management (this issue is an epic) ──
  // A child's epic link lives on the *child*, so the open epic's bundle won't auto-refresh — patch
  // its EpicChildren optimistically, then refetch to reconcile.
  const refreshEpicChildren = (): void => {
    void queryClient.invalidateQueries({ queryKey: boardKeys.detail(issue.id) });
  };
  const addChildToEpic = (child: IssueCard): void => {
    setChildQuery("");
    patchBundle((prev: IssueDetailBundle) => ({ ...prev, epicChildren: [...prev.epicChildren, child] }));
    void BoardService.updateIssue(child.id, { epicId: issue.id }).then(refreshEpicChildren);
  };
  const removeChildFromEpic = (childId: string): void => {
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      epicChildren: prev.epicChildren.filter((c: IssueCard) => c.id !== childId)
    }));
    void BoardService.updateIssue(childId, { epicId: null }).then(refreshEpicChildren);
  };
  const createChildInEpic = (): void => {
    const trimmed: string = childDraft.trim();
    if (!trimmed) return;
    const statusId: string = statuses[0]?.id ?? issue.statusId;
    setChildDraft("");
    void BoardService.createIssue({
      projectKey: issue.projectKey,
      type: "task",
      summary: trimmed,
      statusId,
      epicId: issue.id
    }).then(refreshEpicChildren);
  };

  const handleAddComment = (markdown: string): void => {
    const trimmed: string = markdown.trim();
    if (!trimmed) return;
    // Generate the comment id on the client and have the server honor it, so the optimistic comment
    // IS the persisted comment from the first render — reacting/editing right after posting targets a
    // real row (no "optimistic-…" temp id), and there's no key remount when the bundle refetches.
    const commentId: string = crypto.randomUUID();
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      comments: [
        ...prev.comments,
        {
          id: commentId,
          issueId: issue.id,
          authorUserId: currentUserId(),
          bodyMarkdown: trimmed,
          createdAt: new Date().toISOString(),
          editedAt: null,
          reactions: []
        }
      ]
    }));
    void BoardService.addComment(issue.id, trimmed, commentId);
  };

  const handleDeleteComment = (commentId: string): void => {
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      comments: prev.comments.filter((comment: IssueComment) => comment.id !== commentId)
    }));
    BoardService.deleteComment(commentId, issue.id);
  };

  const handleEditComment = (commentId: string, bodyMarkdown: string): void => {
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      comments: prev.comments.map((comment: IssueComment) =>
        comment.id === commentId ? { ...comment, bodyMarkdown, editedAt: new Date().toISOString() } : comment
      )
    }));
    void BoardService.editComment(commentId, bodyMarkdown, issue.id);
  };

  // Toggle the current user's emoji reaction on a comment, optimistically: add/remove ourselves
  // from that emoji's group (dropping the group when it empties, creating it when new).
  const handleToggleReaction = (commentId: string, emoji: string): void => {
    const me: string = currentUserId();
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      comments: prev.comments.map((comment: IssueComment) => {
        if (comment.id !== commentId) return comment;
        const existing: CommentReaction | undefined = comment.reactions.find(
          (reaction: CommentReaction) => reaction.emoji === emoji
        );
        let reactions: CommentReaction[];
        if (!existing) {
          reactions = [...comment.reactions, { emoji, userIds: [me] }];
        } else if (existing.userIds.includes(me)) {
          const userIds: string[] = existing.userIds.filter((user: string) => user !== me);
          reactions =
            userIds.length === 0
              ? comment.reactions.filter((reaction: CommentReaction) => reaction.emoji !== emoji)
              : comment.reactions.map((reaction: CommentReaction) =>
                  reaction.emoji === emoji ? { ...reaction, userIds } : reaction
                );
        } else {
          reactions = comment.reactions.map((reaction: CommentReaction) =>
            reaction.emoji === emoji ? { ...reaction, userIds: [...reaction.userIds, me] } : reaction
          );
        }
        return { ...comment, reactions };
      })
    }));
    void BoardService.toggleCommentReaction(commentId, emoji, issue.id);
  };

  // Close the reaction picker on an outside click — test the open picker's container ref (set on
  // whichever comment currently owns the picker) instead of a class lookup.
  const reactionPickerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (reactionPickerFor === null) return;
    const onDown = (event: MouseEvent): void => {
      if (!reactionPickerRef.current?.contains(event.target as Node)) setReactionPickerFor(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [reactionPickerFor]);

  const handleRemoveLink = (linkId: string): void => {
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      links: prev.links.filter((entry: IssueLink) => entry.id !== linkId)
    }));
    void BoardService.removeLink(linkId);
  };

  const handleRemoveArtifactLink = (linkId: string): void => {
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      artifactLinks: prev.artifactLinks.filter((entry: ArtifactLink) => entry.id !== linkId)
    }));
    void BoardService.removeArtifactLink(linkId);
  };

  const handleRemoveAttachment = (attachmentId: string): void => {
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      attachments: prev.attachments.filter((entry: Attachment) => entry.id !== attachmentId)
    }));
    void BoardService.removeAttachment(attachmentId);
  };

  const handleToggleSubtaskComplete = (subtask: Issue): void => {
    // Flip toward done / away from done so the check + strikethrough react instantly; the
    // refetch settles the exact non-done status the backend chose.
    const nextStatusId: string =
      subtask.statusId === doneStatusId
        ? (statuses.find((status: BoardStatus) => status.id !== doneStatusId)?.id ?? subtask.statusId)
        : doneStatusId;
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      subtasks: prev.subtasks.map((entry: Issue) =>
        entry.id === subtask.id ? { ...entry, statusId: nextStatusId } : entry
      )
    }));
    void BoardService.toggleSubtaskComplete(subtask.id);
  };

  const handleAddLink = (target: IssueCard, type: IssueLinkType): void => {
    const optimisticLink: IssueLink = {
      id: `optimistic-${Date.now()}`,
      fromIssueId: issue.id,
      toIssueId: target.id,
      type,
      createdAt: new Date().toISOString()
    };
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      links: [...prev.links, optimisticLink],
      // The links list joins against linkedIssues to render the other side — seed it so the new
      // link shows immediately.
      linkedIssues: prev.linkedIssues.some((card: IssueCard) => card.id === target.id)
        ? prev.linkedIssues
        : [...prev.linkedIssues, target]
    }));
    void BoardService.addLink(issue.id, target.id, type);
    setLinkPickerNonce((nonce: number) => nonce + 1);
  };

  const handleAddArtifactLink = (kind: ArtifactKind, refId: string, displayName: string): void => {
    const optimisticLink: ArtifactLink = {
      id: `optimistic-${Date.now()}`,
      issueId: issue.id,
      kind,
      refId,
      displayName
    };
    patchBundle((prev: IssueDetailBundle) => ({
      ...prev,
      artifactLinks: [...prev.artifactLinks, optimisticLink]
    }));
    void BoardService.addArtifactLink(issue.id, kind, refId, displayName);
    setArtifactPickerNonce((nonce: number) => nonce + 1);
  };

  const bodyClass: string = `kuika-board__panel-body kuika-board__panel-body--${layout}`;

  return (
    <div className={bodyClass} style={{ gridTemplateColumns: `minmax(0, 1fr) ${railWidth}px` }}>
      <div className="kuika-board__panel-main">
        <textarea
          className="kuika-board__panel-summary"
          value={summary}
          placeholder={isNewIssue ? "Issue summary…" : undefined}
          autoFocus={isNewIssue}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setSummary(event.target.value);
            // A draft lives in the parent; commit every keystroke so the Create action sees the
            // latest summary without waiting for blur.
            if (isNewIssue) patchIssue({ summary: event.target.value });
          }}
          onBlur={commitSummary}
          onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              (event.target as HTMLTextAreaElement).blur();
            }
          }}
          rows={layout === "page" ? 1 : 2}
        />

        {isNewIssue && aiEnabled && (
          <div className="kuika-board__triage-bar">
            <AiSparkleIcon size={14} className={triageBusy ? "kuika-board__ai-spin" : undefined} />
            <span className="kuika-board__triage-bar-text">
              Let AI set the type, priority, labels and description from your summary
            </span>
            <button
              type="button"
              className="kuika-board__triage-bar-btn"
              onClick={handleTriage}
              disabled={!summary.trim() || triageBusy}
            >
              {triageBusy ? "Triaging…" : "Triage"}
            </button>
          </div>
        )}

        <div>
          <div
            className="kuika-board__panel-section-title"
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <span>Description</span>
            {aiEnabled && (
              <button
                type="button"
                className="kuika-board__ai-action"
                onClick={handleAiDescribe}
                disabled={aiDescribeBusy || (isNewIssue && !summary.trim())}
                title="Draft a description with AI"
              >
                <AiSparkleIcon size={12} className={aiDescribeBusy ? "kuika-board__ai-spin" : undefined} />
                {aiDescribeBusy ? "Drafting…" : "Draft"}
              </button>
            )}
          </div>
          {descriptionLoading ? (
            <DescriptionSkeleton />
          ) : (
            <MarkdownField
              value={issue.descriptionMarkdown}
              placeholder="Add a description… markdown supported."
              emptyHint="Add a description…"
              minHeight={layout === "page" ? 120 : 88}
              issueKeys={issueKeySet}
              onIssueKey={openIssueByKey}
              mentionCandidates={mentionCandidates}
              onChange={(next: string) => {
                patchIssue({ descriptionMarkdown: next });
              }}
            />
          )}
        </div>

        {isNewIssue && issue.type === "epic" && newEpicChildren && (
          <div>
            <div className="kuika-board__panel-section-title">
              <span>
                Child issues
                {newEpicChildren.existing.length + newEpicChildren.toCreate.length > 0 && (
                  <span className="kuika-board__panel-section-count">
                    {newEpicChildren.existing.length + newEpicChildren.toCreate.length}
                  </span>
                )}
              </span>
            </div>
            <div className="kuika-board__subtasks">
              {newEpicChildren.existing.map((child: IssueCard) => (
                <div key={child.id} className="kuika-board__subtask">
                  <span className={`kuika-board__type-icon kuika-board__type-icon--${child.type}`} aria-hidden="true" />
                  <span className="kuika-board__subtask-summary">{child.summary}</span>
                  <span className="kuika-board__subtask-key">{child.key}</span>
                  <button
                    type="button"
                    className="kuika-board__subtask-promote"
                    onClick={() => newEpicChildren.removeExisting(child.id)}
                    aria-label="Remove from epic"
                    title="Remove from epic"
                  >
                    ×
                  </button>
                </div>
              ))}
              {newEpicChildren.toCreate.map((summary: string, index: number) => (
                <div key={`new-${index}`} className="kuika-board__subtask">
                  <span className="kuika-board__type-icon kuika-board__type-icon--task" aria-hidden="true" />
                  <span className="kuika-board__subtask-summary" style={{ fontStyle: "italic" }}>
                    {summary}
                  </span>
                  <span className="kuika-board__subtask-key">new</span>
                  <button
                    type="button"
                    className="kuika-board__subtask-promote"
                    onClick={() => newEpicChildren.removeCreate(index)}
                    aria-label="Remove"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
              <KuikaSelectbox
                key={`epic-child-add-${childAddNonce}`}
                size="sm"
                searchable
                dropdownMatchSelectWidth={false}
                style={{ width: "100%", marginTop: 6 }}
                value=""
                placeholder="Add an existing issue…"
                onSearch={(value: string) => setChildQuery(value)}
                filterOption={() => true}
                onChange={(value: string | number | null) => {
                  const card: IssueCard | undefined = childResults.find((c: IssueCard) => c.id === String(value));
                  if (card) {
                    newEpicChildren.addExisting(card);
                    setChildQuery("");
                    setChildAddNonce((n: number) => n + 1);
                  }
                }}
                options={childSearchOptions(new Set(newEpicChildren.existing.map((c: IssueCard) => c.id)))}
              />
              <form
                className="kuika-board__subtask-add"
                onSubmit={(event: React.FormEvent) => {
                  event.preventDefault();
                  const trimmed: string = childDraft.trim();
                  if (!trimmed) return;
                  newEpicChildren.queueCreate(trimmed);
                  setChildDraft("");
                }}
              >
                <span className="kuika-board__subtask-check kuika-board__subtask-check--empty" aria-hidden="true" />
                <input
                  className="kuika-board__subtask-input"
                  value={childDraft}
                  placeholder="Create an issue in this epic…"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setChildDraft(event.target.value)}
                />
              </form>
            </div>
          </div>
        )}

        {canDiscuss && (
          <>
            {issue.type === "epic" && (
              <div>
                <div className="kuika-board__panel-section-title">
                  <span>
                    Child issues
                    {epicChildren.length > 0 && (
                      <span className="kuika-board__panel-section-count">
                        {epicChildren.filter((c: IssueCard) => c.statusId === doneStatusId).length}/
                        {epicChildren.length}
                      </span>
                    )}
                  </span>
                </div>
                <div className="kuika-board__subtasks">
                  {epicChildren.map((child: IssueCard) => (
                    <div key={child.id} className="kuika-board__subtask">
                      <span
                        className={`kuika-board__type-icon kuika-board__type-icon--${child.type}`}
                        aria-hidden="true"
                      />
                      <span
                        className="kuika-board__subtask-summary"
                        onClick={() => navigate(`/${domainName}/issue/${child.key}`)}
                        role="link"
                        tabIndex={0}
                      >
                        {child.summary}
                      </span>
                      <span className="kuika-board__subtask-key">{child.key}</span>
                      <button
                        type="button"
                        className="kuika-board__subtask-promote"
                        onClick={() => removeChildFromEpic(child.id)}
                        aria-label="Remove from epic"
                        title="Remove from epic"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <KuikaSelectbox
                    key={`epic-child-add-${childAddNonce}`}
                    size="sm"
                    searchable
                    dropdownMatchSelectWidth={false}
                    style={{ width: "100%", marginTop: 6 }}
                    value=""
                    placeholder="Add an existing issue…"
                    onSearch={(value: string) => setChildQuery(value)}
                    filterOption={() => true}
                    onChange={(value: string | number | null) => {
                      const card: IssueCard | undefined = childResults.find((c: IssueCard) => c.id === String(value));
                      if (card) {
                        addChildToEpic(card);
                        setChildAddNonce((n: number) => n + 1);
                      }
                    }}
                    options={childSearchOptions(new Set())}
                  />
                  <form
                    className="kuika-board__subtask-add"
                    onSubmit={(event: React.FormEvent) => {
                      event.preventDefault();
                      createChildInEpic();
                    }}
                  >
                    <span className="kuika-board__subtask-check kuika-board__subtask-check--empty" aria-hidden="true" />
                    <input
                      className="kuika-board__subtask-input"
                      value={childDraft}
                      placeholder="Create an issue in this epic…"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => setChildDraft(event.target.value)}
                    />
                  </form>
                </div>
              </div>
            )}
            {issue.type !== "epic" && (
              <div>
                <div
                  className="kuika-board__panel-section-title"
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <span>
                    Subtasks
                    {subtasks.length > 0 && (
                      <span className="kuika-board__panel-section-count">
                        {subtasks.filter((entry: Issue) => entry.statusId === doneStatusId).length}/{subtasks.length}
                      </span>
                    )}
                  </span>
                  {aiEnabled && (
                    <button
                      type="button"
                      className="kuika-board__ai-action"
                      onClick={handleAiSubtasks}
                      disabled={aiSubtasksBusy}
                      title="Suggest subtasks with AI"
                    >
                      <AiSparkleIcon size={12} className={aiSubtasksBusy ? "kuika-board__ai-spin" : undefined} />
                      {aiSubtasksBusy ? "Thinking…" : "Suggest"}
                    </button>
                  )}
                </div>
                <div className="kuika-board__subtasks">
                  {aiSubtasks.map((summary: string) => (
                    <div key={`ai-${summary}`} className="kuika-board__subtask kuika-board__subtask--suggested">
                      <button
                        type="button"
                        className="kuika-board__subtask-check kuika-board__subtask-check--empty"
                        onClick={() => acceptAiSubtask(summary)}
                        aria-label="Add suggested subtask"
                        title="Add this subtask"
                        style={{ cursor: "pointer", color: "var(--blue-6)" }}
                      >
                        +
                      </button>
                      <span className="kuika-board__subtask-summary" style={{ opacity: 0.85, fontStyle: "italic" }}>
                        {summary}
                      </span>
                      <button
                        type="button"
                        className="kuika-board__subtask-promote"
                        onClick={() =>
                          setAiSubtasks((prev: string[]): string[] =>
                            prev.filter((entry: string): boolean => entry !== summary)
                          )
                        }
                        aria-label="Dismiss suggestion"
                        title="Dismiss"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {subtasks.map((subtask: Issue) => {
                    const completed: boolean = subtask.statusId === doneStatusId;
                    return (
                      <div key={subtask.id} className="kuika-board__subtask">
                        <button
                          type="button"
                          className={`kuika-board__subtask-check${completed ? " kuika-board__subtask-check--done" : ""}`}
                          onClick={() => handleToggleSubtaskComplete(subtask)}
                          aria-label={completed ? "Mark as incomplete" : "Mark as complete"}
                        >
                          {completed && (
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path
                                d="M2 5L4 7L8 3"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                        <span
                          className={`kuika-board__subtask-summary${completed ? " kuika-board__subtask-summary--done" : ""}`}
                          onClick={() => navigate(`/${domainName}/issue/${subtask.key}`)}
                          role="link"
                          tabIndex={0}
                        >
                          {subtask.summary}
                        </span>
                        <span className="kuika-board__subtask-key">{subtask.key}</span>
                        <button
                          type="button"
                          className="kuika-board__subtask-promote"
                          onClick={() => BoardService.promoteSubtask(subtask.id, issue.id)}
                          aria-label="Promote to a standalone issue"
                          title="Promote to a standalone issue"
                        >
                          ↗
                        </button>
                      </div>
                    );
                  })}
                  <form
                    className="kuika-board__subtask-add"
                    onSubmit={(event: React.FormEvent) => {
                      event.preventDefault();
                      const trimmed: string = subtaskDraft.trim();
                      if (!trimmed) return;
                      BoardService.createSubtask(issue.id, trimmed);
                      setSubtaskDraft("");
                    }}
                  >
                    <span className="kuika-board__subtask-check kuika-board__subtask-check--empty" aria-hidden="true" />
                    <input
                      className="kuika-board__subtask-input"
                      value={subtaskDraft}
                      placeholder="Add a subtask…"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSubtaskDraft(event.target.value)}
                    />
                  </form>
                </div>
              </div>
            )}

            <div>
              <div className="kuika-board__panel-section-title">Links</div>
              <div className="kuika-board__links">
                {links.map((entry) => (
                  <div key={entry.link.id} className="kuika-board__link">
                    <span className="kuika-board__link-relation">{LINK_TYPE_LABELS[entry.relation]}</span>
                    <span
                      className="kuika-board__link-summary"
                      onClick={() => navigate(`/${domainName}/issue/${entry.other.key}`)}
                      role="link"
                      tabIndex={0}
                    >
                      <span className="kuika-board__link-key">{entry.other.key}</span>
                      {entry.other.summary}
                    </span>
                    <button
                      type="button"
                      className="kuika-board__link-remove"
                      onClick={() => handleRemoveLink(entry.link.id)}
                      aria-label="Remove link"
                      title="Remove link"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <div className="kuika-board__link-add">
                  <KuikaSelectbox
                    size="sm"
                    dropdownMatchSelectWidth={false}
                    className="kuika-board__link-type-select"
                    value={linkType}
                    onChange={(value: string | number | null) => setLinkType((value as IssueLinkType) ?? "blocks")}
                    options={LINK_TYPES.map((type: IssueLinkType) => ({
                      value: type,
                      label: LINK_TYPE_LABELS[type]
                    }))}
                  />
                  <KuikaSelectbox
                    key={linkPickerNonce}
                    size="sm"
                    searchable
                    allowClear
                    dropdownMatchSelectWidth={false}
                    className="kuika-board__link-target-select"
                    style={{ flex: 1, minWidth: 0 }}
                    placeholder="Search an issue to link…"
                    value={linkTargetKey || undefined}
                    onSearch={(value: string) => setLinkQuery(value)}
                    filterOption={() => true}
                    onChange={async (value: string | number | null) => {
                      setLinkError("");
                      if (!value) {
                        setLinkTargetKey("");
                        return;
                      }
                      const target: IssueCard | undefined = linkResults.find(
                        (card: IssueCard) => card.id === String(value)
                      );
                      if (!target || target.id === issue.id) {
                        setLinkError("No matching issue.");
                        return;
                      }
                      const wouldCycle: boolean = await BoardService.wouldCreateBlockingCycle(
                        issue.id,
                        target.id,
                        linkType
                      );
                      if (wouldCycle) {
                        setLinkError(`That would create a dependency cycle with ${target.key}.`);
                        return;
                      }
                      handleAddLink(target, linkType);
                      setLinkTargetKey("");
                      setLinkQuery("");
                    }}
                    options={linkResults.map((entry: IssueCard) => ({
                      value: entry.id,
                      searchLabel: `${entry.key} ${entry.summary}`,
                      label: (
                        <span className="kuika-board__link-option">
                          <span className="kuika-board__link-option-key">{entry.key}</span>
                          {entry.summary}
                        </span>
                      )
                    }))}
                  />
                </div>
                {linkError && <div className="kuika-board__attachment-error">{linkError}</div>}
              </div>
            </div>

            <div>
              <div className="kuika-board__panel-section-title">
                Linked artifacts
                {artifactLinks.length > 0 && (
                  <span className="kuika-board__panel-section-count">{artifactLinks.length}</span>
                )}
              </div>
              <div className="kuika-board__artifacts">
                {artifactLinks.map((link: ArtifactLink) => {
                  // Resolve the artifact's current name (survives renames); fall back to the stored
                  // snapshot for free-text kinds or before the lists load.
                  const currentName: string = artifactNames[`${link.kind}:${link.refId}`] ?? link.displayName;
                  return (
                    <div key={link.id} className="kuika-board__artifact">
                      <span
                        className="kuika-board__artifact-dot"
                        style={{ background: ARTIFACT_KIND_COLOR[link.kind] }}
                        aria-hidden="true"
                      />
                      <span className="kuika-board__artifact-kind">{ARTIFACT_KIND_LABELS[link.kind]}</span>
                      {(link.kind === "screen" || link.kind === "report") && domainName ? (
                        <button
                          type="button"
                          className="kuika-board__artifact-name kuika-board__artifact-name--link"
                          onClick={() => navigate(`/${domainName}/sd/${link.refId}`)}
                          title="Open in the designer"
                        >
                          {currentName}
                        </button>
                      ) : (
                        <span className="kuika-board__artifact-name">{currentName}</span>
                      )}
                      <button
                        type="button"
                        className="kuika-board__artifact-remove"
                        onClick={() => handleRemoveArtifactLink(link.id)}
                        aria-label="Remove link"
                        title="Remove link"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <form
                  className="kuika-board__artifact-add"
                  onSubmit={(event: React.FormEvent) => {
                    event.preventDefault();
                    const trimmed: string = artifactName.trim();
                    if (!trimmed) return;
                    handleAddArtifactLink(artifactKind, trimmed.toLowerCase().replace(/\s+/g, "-"), trimmed);
                    setArtifactName("");
                  }}
                >
                  <KuikaSelectbox
                    size="sm"
                    dropdownMatchSelectWidth={false}
                    className="kuika-board__link-type-select"
                    value={artifactKind}
                    onChange={(value: string | number | null) => setArtifactKind((value as ArtifactKind) ?? "screen")}
                    options={(Object.keys(ARTIFACT_KIND_LABELS) as ArtifactKind[]).map((kind: ArtifactKind) => ({
                      value: kind,
                      label: ARTIFACT_KIND_LABELS[kind]
                    }))}
                  />
                  {artifactKindHasPicker(artifactKind) && platformProjectId ? (
                    <KuikaSelectbox
                      key={artifactPickerNonce}
                      size="sm"
                      searchable
                      allowClear
                      dropdownMatchSelectWidth={false}
                      className="kuika-board__link-target-select"
                      style={{ flex: 1, minWidth: 0 }}
                      placeholder={
                        addOptions.length > 0
                          ? `Find a ${ARTIFACT_KIND_LABELS[artifactKind].toLowerCase()}…`
                          : `No ${ARTIFACT_KIND_LABELS[artifactKind].toLowerCase()}s in this project`
                      }
                      value={undefined}
                      onChange={(value: string | number | null) => {
                        if (!value) return;
                        const picked: ArtifactOption | undefined = addOptions.find(
                          (option: ArtifactOption) => option.id === String(value)
                        );
                        if (!picked) return;
                        handleAddArtifactLink(artifactKind, picked.id, picked.name);
                      }}
                      options={addOptions.map((option: ArtifactOption) => ({
                        value: option.id,
                        searchLabel: option.name,
                        label: option.name
                      }))}
                    />
                  ) : (
                    <input
                      className="kuika-board__link-input"
                      value={artifactName}
                      placeholder="Artifact name (e.g. Dashboard)"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => setArtifactName(event.target.value)}
                    />
                  )}
                </form>
              </div>
            </div>

            <div>
              <div className="kuika-board__panel-section-title">
                Attachments
                {attachments.length > 0 && (
                  <span className="kuika-board__panel-section-count">{attachments.length}</span>
                )}
              </div>
              <div
                className={`kuika-board__dropzone${isDraggingFile ? " kuika-board__dropzone--active" : ""}`}
                onDragOver={(event: React.DragEvent) => {
                  event.preventDefault();
                  setIsDraggingFile(true);
                }}
                onDragLeave={() => setIsDraggingFile(false)}
                onDrop={(event: React.DragEvent) => {
                  event.preventDefault();
                  setIsDraggingFile(false);
                  if (event.dataTransfer.files.length > 0) void ingestFiles(event.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
              >
                <span className="kuika-board__dropzone-icon" aria-hidden="true">
                  <KuikaSvg name="upload" width={18} height={18} />
                </span>
                <span className="kuika-board__dropzone-label">
                  Drop files, paste, or <em>browse</em>
                </span>
                <span className="kuika-board__dropzone-hint">Up to {formatBytes(MAX_ATTACHMENT_BYTES)} each</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="kuika-board__file-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (event.target.files) void ingestFiles(event.target.files);
                    event.target.value = "";
                  }}
                />
              </div>
              {attachmentError && <div className="kuika-board__attachment-error">{attachmentError}</div>}
              {(attachments.length > 0 || uploads.length > 0) && (
                <div className="kuika-board__attachments">
                  {attachments.map((attachment: Attachment) => (
                    <AttachmentRow
                      key={attachment.id}
                      attachment={attachment}
                      onRemove={() => handleRemoveAttachment(attachment.id)}
                      onPreview={setPreviewing}
                    />
                  ))}
                  {uploads.map((upload: PendingUpload) => (
                    <div
                      key={`upload-${upload.id}`}
                      className="kuika-board__attachment kuika-board__attachment--uploading"
                    >
                      <span className="kuika-board__attachment-tile" data-kind="file" aria-hidden="true">
                        <span className="kuika-board__attachment-spinner" />
                      </span>
                      <div className="kuika-board__attachment-meta">
                        <span className="kuika-board__attachment-name" title={upload.name}>
                          {upload.name}
                        </span>
                        <span className="kuika-board__attachment-size">Uploading · {formatBytes(upload.size)}</span>
                      </div>
                      <span className="kuika-board__attachment-progress" aria-hidden="true" />
                    </div>
                  ))}
                </div>
              )}
              {previewing && <AttachmentLightbox attachment={previewing} onClose={() => setPreviewing(null)} />}
            </div>

            <div>
              <div className="kuika-board__activity-header">
                <div className="kuika-board__activity-tabs" role="tablist">
                  {(["comments", "history"] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={activityTab === tab}
                      className={`kuika-board__activity-tab${activityTab === tab ? " kuika-board__activity-tab--active" : ""}`}
                      onClick={() => setActivityTab(tab)}
                    >
                      {tab === "comments" ? "Comments" : "History"}
                      <span className="kuika-board__activity-tab-count">
                        {tab === "comments" ? issueComments.length : issueActivity.length}
                      </span>
                    </button>
                  ))}
                </div>
                {aiEnabled && (
                  <button
                    type="button"
                    className="kuika-board__ai-action"
                    onClick={() => {
                      if (aiSummaryBusy) return;
                      setAiSummaryBusy(true);
                      void BoardAiApi.summarizeIssue(issue.id)
                        .then((result: BoardAiTextResult): void => setAiSummary(result.text))
                        .catch((): void => undefined)
                        .finally((): void => setAiSummaryBusy(false));
                    }}
                    disabled={aiSummaryBusy}
                    title="Summarize this issue and its discussion"
                  >
                    <AiSparkleIcon size={12} className={aiSummaryBusy ? "kuika-board__ai-spin" : undefined} />
                    {aiSummaryBusy ? "Summarizing…" : "Summarize"}
                  </button>
                )}
              </div>

              {aiSummary && (
                <div className="kuika-board__ai-card">
                  <div className="kuika-board__ai-card-label">
                    <AiSparkleIcon size={12} />
                    AI summary
                  </div>
                  <div className="kuika-board__ai-card-body">
                    <MarkedReact gfm breaks value={aiSummary} renderer={SAFE_MARKDOWN_RENDERER} />
                  </div>
                  <button
                    type="button"
                    className="kuika-board__ai-card-dismiss"
                    onClick={() => setAiSummary(null)}
                    aria-label="Dismiss summary"
                  >
                    ×
                  </button>
                </div>
              )}

              {activityTab === "history" && (
                <div className="kuika-board__activity-stream">
                  {detailLoading && <ActivitySkeleton />}
                  {!detailLoading && issueActivity.length === 0 && (
                    <div className="kuika-board__activity-empty">No history yet.</div>
                  )}
                  {!detailLoading && issueActivity.length > 0 && (
                    <ol className="kuika-board__history">
                      {issueActivity.map((entry: IssueActivity) => (
                        <li key={entry.id} className="kuika-board__history-event">
                          <BoardAvatar userId={entry.actorUserId} className="kuika-board__history-avatar" ariaHidden />
                          <div className="kuika-board__history-body">
                            <div className="kuika-board__history-line">
                              <span className="kuika-board__history-actor">
                                {entry.actorUserId ? displayNameOf(entry.actorUserId) : "Automation"}
                              </span>{" "}
                              <span className="kuika-board__history-text">{entry.text}</span>
                              <time
                                className="kuika-board__history-time"
                                dateTime={entry.at}
                                title={dayjs(entry.at).format("MMM D, YYYY · HH:mm")}
                              >
                                {relativeTime(entry.at)}
                              </time>
                            </div>
                            {entry.changes && entry.changes.length > 0 && (
                              <div className="kuika-board__history-diff">
                                {entry.changes.map((change: FieldChange, index: number) => (
                                  <div key={`${entry.id}-${index}`} className="kuika-board__history-diff-row">
                                    <span className="kuika-board__history-diff-field">{change.field}</span>
                                    <span className="kuika-board__history-diff-from">{change.from || "—"}</span>
                                    <span className="kuika-board__history-diff-arrow" aria-hidden="true">
                                      →
                                    </span>
                                    <span className="kuika-board__history-diff-to">{change.to || "—"}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}

              {activityTab === "comments" && (
                <div className="kuika-board__activity-stream">
                  {detailLoading && <CommentsSkeleton />}
                  {!detailLoading &&
                    issueComments.map((comment: IssueComment) => {
                      const isOwn: boolean = comment.authorUserId === currentUserId();
                      const isEditing: boolean = editingCommentId === comment.id;
                      const authorName: string = comment.authorUserId
                        ? displayNameOf(comment.authorUserId)
                        : "Automation";
                      return (
                        <div key={comment.id} className="kuika-board__comment">
                          <BoardAvatar
                            userId={comment.authorUserId}
                            className="kuika-board__comment-avatar"
                            ariaHidden
                          />
                          <div className="kuika-board__comment-body">
                            <div className="kuika-board__comment-head">
                              <span className="kuika-board__comment-author">{authorName}</span>
                              <span className="kuika-board__comment-time">
                                {dayjs(comment.createdAt).format("MMM D, HH:mm")}
                                {comment.editedAt && " · edited"}
                              </span>
                              {isOwn && !isEditing && (
                                <span className="kuika-board__comment-actions">
                                  <button
                                    type="button"
                                    className="kuika-board__comment-action"
                                    onClick={() => setEditingCommentId(comment.id)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="kuika-board__comment-action"
                                    onClick={() => {
                                      void confirmDialog({
                                        title: "Delete comment",
                                        message: "Delete this comment? This can't be undone.",
                                        confirmLabel: "Delete",
                                        danger: true
                                      }).then((ok: boolean) => {
                                        if (ok) handleDeleteComment(comment.id);
                                      });
                                    }}
                                  >
                                    Delete
                                  </button>
                                </span>
                              )}
                            </div>
                            {isEditing ? (
                              <CommentEditor
                                candidates={mentionCandidates}
                                issueKeys={issueKeySet}
                                onIssueKey={openIssueByKey}
                                initialMarkdown={comment.bodyMarkdown}
                                placeholder="Edit comment…"
                                submitLabel="Save"
                                autoFocus
                                onSubmit={(markdown: string) => {
                                  handleEditComment(comment.id, markdown);
                                  setEditingCommentId(null);
                                }}
                                onCancel={() => setEditingCommentId(null)}
                              />
                            ) : (
                              <div className="kuika-board__comment-text kuika-md-field__prose">
                                <MarkedReact
                                  gfm
                                  breaks
                                  renderer={commentRenderer}
                                  value={highlightMentions(comment.bodyMarkdown, mentionCandidates)}
                                />
                              </div>
                            )}
                            <div
                              ref={reactionPickerFor === comment.id ? reactionPickerRef : undefined}
                              className="kuika-board__comment-reactions"
                            >
                              {comment.reactions.map((reaction: CommentReaction) => {
                                const mine: boolean = reaction.userIds.includes(currentUserId());
                                return (
                                  <button
                                    key={reaction.emoji}
                                    type="button"
                                    className={`kuika-board__reaction${mine ? " kuika-board__reaction--mine" : ""}`}
                                    title={reaction.userIds.map((userId: string) => displayNameOf(userId)).join(", ")}
                                    onClick={() => handleToggleReaction(comment.id, reaction.emoji)}
                                  >
                                    <span className="kuika-board__reaction-emoji">{reaction.emoji}</span>
                                    <span className="kuika-board__reaction-count">{reaction.userIds.length}</span>
                                  </button>
                                );
                              })}
                              <button
                                type="button"
                                className="kuika-board__reaction-add"
                                title="Add reaction"
                                aria-label="Add reaction"
                                onClick={() =>
                                  setReactionPickerFor((current: string | null) =>
                                    current === comment.id ? null : comment.id
                                  )
                                }
                              >
                                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                  <circle cx="7" cy="8" r="5" stroke="currentColor" strokeWidth="1.2" />
                                  <circle cx="5.3" cy="7" r="0.7" fill="currentColor" />
                                  <circle cx="8.7" cy="7" r="0.7" fill="currentColor" />
                                  <path
                                    d="M5.2 9.4a2.6 2.6 0 0 0 3.6 0"
                                    stroke="currentColor"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                  />
                                  <path
                                    d="M12.5 3v3M14 4.5h-3"
                                    stroke="currentColor"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                              {reactionPickerFor === comment.id && (
                                <div className="kuika-board__reaction-pop">
                                  <EmojiPicker
                                    onPick={(emoji: string) => {
                                      handleToggleReaction(comment.id, emoji);
                                      setReactionPickerFor(null);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            {extractCommentUrls(comment.bodyMarkdown)
                              .filter((url: string) => !dismissedPreviews.has(`${comment.id}|${url}`))
                              .map((url: string) => (
                                <LinkPreviewCard
                                  key={url}
                                  url={url}
                                  onDismiss={() =>
                                    setDismissedPreviews((prev: Set<string>) => {
                                      const next: Set<string> = new Set<string>(prev);
                                      next.add(`${comment.id}|${url}`);
                                      return next;
                                    })
                                  }
                                />
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  {!detailLoading && issueComments.length === 0 && (
                    <div className="kuika-board__activity-empty">No comments yet.</div>
                  )}
                </div>
              )}

              {activityTab === "comments" && (
                <CommentEditor
                  candidates={mentionCandidates}
                  issueKeys={issueKeySet}
                  onIssueKey={openIssueByKey}
                  onSubmit={handleAddComment}
                  placeholder="Leave a comment…"
                />
              )}
            </div>
          </>
        )}
      </div>

      <div className="kuika-board__panel-rail">
        <div
          className="kuika-board__rail-resize"
          onPointerDown={onRailResizeDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize details panel"
          title="Drag to resize"
        />
        {project && (
          <div className="kuika-board__rail-project">
            <span className="kuika-board__rail-project-dot" style={{ background: project.color }} aria-hidden="true" />
            <span className="kuika-board__rail-project-key">{project.key}</span>
            <span className="kuika-board__rail-project-name">{project.name}</span>
          </div>
        )}

        <div className="kuika-board__rail-section">
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Status</span>
            <StatusPicker status={currentStatus} options={statusOptions} onPick={pickStatus} />
          </div>
        </div>

        <div className="kuika-board__rail-section">
          <div className="kuika-board__rail-section-title">People</div>
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Assignee</span>
            <div className="kuika-board__rail-person">
              <KuikaSelectbox
                size="sm"
                searchable
                dropdownMatchSelectWidth={false}
                className="kuika-board__assignee-select"
                style={{ flex: 1, minWidth: 0 }}
                placeholder="Unassigned"
                value={issue.assigneeUserId ?? ""}
                onChange={(value: string | number | null) =>
                  patchIssue({ assigneeUserId: value ? String(value) : null })
                }
                options={assigneeOptions}
              />
            </div>
          </div>
          {isSaved && (
            <div className="kuika-board__rail-row">
              <span className="kuika-board__rail-label">Reporter</span>
              <div className="kuika-board__rail-person">
                <BoardAvatar userId={issue.reporterUserId} className="kuika-board__rail-avatar" ariaHidden />
                <span className="kuika-board__rail-value">{displayNameOf(issue.reporterUserId)}</span>
              </div>
            </div>
          )}
          {isSaved && (
            <div className="kuika-board__rail-row">
              <span className="kuika-board__rail-label">Watchers</span>
              {issue.watcherUserIds.length === 0 ? (
                <span className="kuika-board__rail-value kuika-board__rail-empty">No watchers</span>
              ) : (
                <span
                  className="kuika-board__rail-avatars"
                  title={issue.watcherUserIds.map((watcher: string) => displayNameOf(watcher)).join(", ")}
                >
                  {issue.watcherUserIds.slice(0, 5).map((watcher: string) => (
                    <BoardAvatar
                      key={watcher}
                      userId={watcher}
                      className="kuika-board__rail-avatar kuika-board__rail-avatar--stack"
                      ariaHidden
                    />
                  ))}
                  {issue.watcherUserIds.length > 5 && (
                    <span className="kuika-board__rail-avatar-more">+{issue.watcherUserIds.length - 5}</span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="kuika-board__rail-section">
          <div className="kuika-board__rail-section-title">Details</div>
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Type</span>
            <div className="kuika-board__rail-field">
              <span className={`kuika-board__type-icon kuika-board__type-icon--${issue.type}`} aria-hidden="true" />
              <KuikaSelectbox
                size="sm"
                dropdownMatchSelectWidth={false}
                style={{ flex: 1, minWidth: 0 }}
                value={issue.type}
                onChange={(value: string | number | null) => patchIssue({ type: (value as IssueType) ?? "task" })}
                options={TYPES.map((type: IssueType) => ({ value: type, label: ISSUE_TYPE_LABELS[type] }))}
              />
            </div>
          </div>
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Priority</span>
            <div className="kuika-board__rail-field">
              <span
                className="kuika-board__rail-dot"
                style={{ background: PRIORITY_COLOR[issue.priority] }}
                aria-hidden="true"
              />
              <KuikaSelectbox
                size="sm"
                dropdownMatchSelectWidth={false}
                style={{ flex: 1, minWidth: 0 }}
                value={issue.priority}
                onChange={(value: string | number | null) =>
                  patchIssue({ priority: (value as IssuePriority) ?? "none" })
                }
                options={PRIORITY_ORDER.map((priority: IssuePriority) => ({
                  value: priority,
                  label: PRIORITY_LABELS[priority]
                }))}
              />
            </div>
          </div>
          <div className="kuika-board__rail-row kuika-board__rail-row--stacked">
            <span className="kuika-board__rail-label">Labels</span>
            <div className="kuika-board__rail-labels">
              {issue.labels.length > 0 && (
                <div className="kuika-board__rail-chips">
                  {issue.labels.map((label: string) => {
                    const def: LabelDef | undefined = config.labels.find((entry: LabelDef) => entry.name === label);
                    return (
                      <span
                        key={label}
                        className="kuika-board__rail-chip"
                        style={def ? { color: def.color, borderColor: def.color } : undefined}
                      >
                        {label}
                        <button
                          type="button"
                          className="kuika-board__rail-chip-remove"
                          aria-label={`Remove label ${label}`}
                          onClick={() =>
                            patchIssue({
                              labels: issue.labels.filter((entry: string) => entry !== label)
                            })
                          }
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <input
                className="kuika-board__rail-input"
                value={labelDraft}
                placeholder="Add a label…"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLabelDraft(event.target.value)}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key !== "Enter" && event.key !== ",") return;
                  event.preventDefault();
                  const name: string = labelDraft.trim();
                  if (name && !issue.labels.includes(name)) {
                    BoardService.ensureLabel(name);
                    patchIssue({ labels: [...issue.labels, name] });
                  }
                  setLabelDraft("");
                }}
              />
            </div>
          </div>
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Fix version</span>
            <KuikaSelectbox
              size="sm"
              dropdownMatchSelectWidth={false}
              style={{ flex: 1, minWidth: 0 }}
              value={issue.fixVersionId ?? ""}
              onChange={(value: string | number | null) => patchIssue({ fixVersionId: value ? String(value) : null })}
              options={[
                { value: "", label: "None" },
                ...config.versions
                  .filter((entry: Version) => entry.projectKey === issue.projectKey)
                  .map((entry: Version) => ({
                    value: entry.id,
                    label: `${entry.name}${entry.released ? " (released)" : ""}`
                  }))
              ]}
            />
          </div>
          {issue.type !== "epic" && (
            <div className="kuika-board__rail-row">
              <span className="kuika-board__rail-label">Epic</span>
              <KuikaSelectbox
                size="sm"
                searchable
                dropdownMatchSelectWidth={false}
                style={{ flex: 1, minWidth: 0 }}
                value={issue.epicId ?? ""}
                onSearch={(value: string) => setEpicQuery(value)}
                filterOption={() => true}
                onChange={(value: string | number | null) => {
                  patchIssue({ epicId: value ? String(value) : null });
                  setEpicQuery("");
                }}
                options={[
                  { value: "", label: "No epic" },
                  ...(epicCard && !epicResults.some((card: IssueCard) => card.id === epicCard.id)
                    ? [{ value: epicCard.id, label: `${epicCard.key} · ${epicCard.summary}` }]
                    : []),
                  ...epicResults.map((entry: IssueCard) => ({
                    value: entry.id,
                    label: `${entry.key} · ${entry.summary}`
                  }))
                ]}
              />
            </div>
          )}
          {issue.type === "epic" && (
            <>
              <div className="kuika-board__rail-row">
                <span className="kuika-board__rail-label">Epic name</span>
                <DebouncedRailInput
                  resyncKey={issue.id}
                  initial={issue.epicName ?? ""}
                  placeholder={issue.summary}
                  commit={(raw: string) => patchIssue({ epicName: raw.trim() ? raw.trim() : null })}
                />
              </div>
              <div className="kuika-board__rail-row">
                <span className="kuika-board__rail-label">Epic color</span>
                <div className="kuika-board__epic-swatches">
                  {EPIC_COLORS.map((color: string) => (
                    <button
                      key={color}
                      type="button"
                      className={`kuika-board__epic-swatch${issue.epicColor === color ? " kuika-board__epic-swatch--active" : ""}`}
                      style={{ background: color }}
                      onClick={() => patchIssue({ epicColor: color })}
                      aria-label={`Epic colour ${color}`}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="kuika-board__rail-section">
          <div className="kuika-board__rail-section-title">Time tracking</div>
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Story points</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <DebouncedRailInput
                type="number"
                min={0}
                max={99}
                placeholder="—"
                resyncKey={issue.id}
                initial={issue.storyPoints != null ? String(issue.storyPoints) : ""}
                commit={commitStoryPoints}
              />
              {aiEnabled && canUseIssueAi && (
                <button
                  type="button"
                  className="kuika-board__ai-icon-btn"
                  onClick={handleAiEstimate}
                  disabled={aiEstimateBusy}
                  aria-label="Estimate story points with AI"
                  title="Estimate with AI"
                >
                  <AiSparkleIcon size={14} className={aiEstimateBusy ? "kuika-board__ai-spin" : undefined} />
                </button>
              )}
            </div>
          </div>
          {aiEstimate?.storyPoints != null && (
            <div className="kuika-board__rail-row" style={{ justifyContent: "flex-end", gap: 8 }}>
              <span style={{ fontSize: 12, color: "var(--dark-7)" }} title={aiEstimate.rationale}>
                Suggested: <strong>{aiEstimate.storyPoints}</strong> pts
              </span>
              <button type="button" className="kuika-board__ai-action" onClick={applyAiEstimate}>
                Apply
              </button>
              <button
                type="button"
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12 }}
                onClick={() => setAiEstimate(null)}
                aria-label="Dismiss estimate"
              >
                ×
              </button>
            </div>
          )}
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Due date</span>
            <BoardDatePicker
              value={issue.dueDate}
              placeholder="No due date"
              onChange={(next: string | null) => patchIssue({ dueDate: next })}
            />
          </div>
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Estimate</span>
            <DebouncedRailInput
              placeholder="e.g. 2h"
              resyncKey={issue.id}
              initial={issue.originalEstimateMin != null ? formatMinutes(issue.originalEstimateMin) : ""}
              commit={(raw: string) => {
                const next: number | null = parseMinutes(raw);
                if (next !== issue.originalEstimateMin) {
                  patchIssue({ originalEstimateMin: next });
                }
              }}
            />
          </div>
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Remaining</span>
            <DebouncedRailInput
              placeholder="e.g. 1h 30m"
              resyncKey={issue.id}
              initial={issue.remainingEstimateMin != null ? formatMinutes(issue.remainingEstimateMin) : ""}
              commit={(raw: string) => {
                const next: number | null = parseMinutes(raw);
                if (next !== issue.remainingEstimateMin) {
                  patchIssue({ remainingEstimateMin: next });
                }
              }}
            />
          </div>
          <div className="kuika-board__rail-row">
            <span className="kuika-board__rail-label">Time spent</span>
            <span className="kuika-board__rail-value">
              <DebouncedRailInput
                placeholder="0"
                resyncKey={issue.id}
                initial={issue.timeSpentMin != null ? formatMinutes(issue.timeSpentMin) : ""}
                commit={(raw: string) => {
                  const next: number | null = parseMinutes(raw);
                  if (next !== issue.timeSpentMin) {
                    patchIssue({ timeSpentMin: next });
                  }
                }}
              />
              <button
                type="button"
                className="kuika-board__rail-quick-log"
                title="Log 15 minutes"
                onClick={() =>
                  patchIssue({
                    timeSpentMin: (issue.timeSpentMin ?? 0) + 15,
                    remainingEstimateMin:
                      issue.remainingEstimateMin != null ? Math.max(0, issue.remainingEstimateMin - 15) : null
                  })
                }
              >
                +15m
              </button>
            </span>
          </div>
        </div>

        {isSaved && (
          <>
            <div className="kuika-board__rail-section">
              <div className="kuika-board__rail-section-title">Dates</div>
              <div className="kuika-board__rail-row">
                <span className="kuika-board__rail-label">Created</span>
                <span className="kuika-board__rail-value">{dayjs(issue.createdAt).format("MMM D, YYYY")}</span>
              </div>
              <div className="kuika-board__rail-row">
                <span className="kuika-board__rail-label">Updated</span>
                <span className="kuika-board__rail-value">{dayjs(issue.updatedAt).format("MMM D, YYYY HH:mm")}</span>
              </div>
            </div>

            <div className="kuika-board__rail-section">
              {projectCustomFields.length > 0 && <div className="kuika-board__rail-section-title">Custom fields</div>}
              {projectCustomFields.map((def: CustomFieldDef) => {
                const value: string = issue.customFields[def.id] ?? "";
                return (
                  <div key={def.id} className="kuika-board__rail-row">
                    <span className="kuika-board__rail-label">{def.name}</span>
                    {def.type === "user" ? (
                      <KuikaSelectbox
                        size="sm"
                        searchable
                        dropdownMatchSelectWidth={false}
                        style={{ width: "100%" }}
                        placeholder="—"
                        value={value}
                        onChange={(next: string | number | null) =>
                          BoardService.setCustomFieldValue(issue.id, def.id, next ? String(next) : "")
                        }
                        options={userFieldOptions}
                      />
                    ) : def.type === "select" ? (
                      <KuikaSelectbox
                        size="sm"
                        dropdownMatchSelectWidth={false}
                        style={{ width: "100%" }}
                        value={value}
                        onChange={(next: string | number | null) =>
                          BoardService.setCustomFieldValue(issue.id, def.id, next ? String(next) : "")
                        }
                        options={[
                          { value: "", label: "—" },
                          ...def.options.map((option: string) => ({ value: option, label: option }))
                        ]}
                      />
                    ) : def.type === "checkbox" ? (
                      <span className="kuika-board__rail-value">
                        <input
                          type="checkbox"
                          checked={value === "true"}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            BoardService.setCustomFieldValue(issue.id, def.id, event.target.checked ? "true" : "")
                          }
                        />
                      </span>
                    ) : def.type === "date" ? (
                      <BoardDatePicker
                        value={value || null}
                        onChange={(next: string | null) =>
                          BoardService.setCustomFieldValue(issue.id, def.id, next ?? "")
                        }
                      />
                    ) : (
                      <DebouncedRailInput
                        type={def.type === "number" ? "number" : "text"}
                        resyncKey={`${issue.id}:${def.id}`}
                        initial={value}
                        commit={(raw: string) => {
                          if (raw !== (issue.customFields[def.id] ?? "")) {
                            BoardService.setCustomFieldValue(issue.id, def.id, raw);
                          }
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IssueDetail;
